/**
 * TEAM API — gestione dei team di sito del CRM (com.liferay.portal.kernel.model.Team).
 *
 * Il backend espone:
 *   - GET    /teams/{groupId}          lista dei team del sito, ciascuno con i membri
 *   - POST   /teams/{groupId}          crea un nuovo team nel sito
 *   - PATCH  /team/{teamId}            aggiorna nome/descrizione di un team
 *   - DELETE /team/{teamId}            elimina un team
 *   - POST   /team/{teamId}/member/{userId}   aggiunge un membro
 *   - DELETE /team/{teamId}/member/{userId}   rimuove un membro
 *
 * Creazione, modifica, eliminazione e gestione membri sono riservate agli
 * amministratori CRM (enforce reale lato server via Permission Framework:
 * MANAGE_TEAMS sul sito). La lista non è cached: dopo ogni mutazione va rifetchata.
 */

import type { Page } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import { toMember, type Member, type MemberDto } from '../user/userApi';

/**
 * Shape del Team ritornato da Liferay backend (`it.dvel.crm.rest.dto.v1_0.Team`).
 * I membri sono Member "ricchi" (firstName/lastName/role/portraitURL/status separati).
 */
interface TeamDto {
  teamId: number;
  groupId: number | null;
  name: string | null;
  description: string | null;
  // Il backend serializza i membri col nome di campo grezzo (emailAddress, ...):
  // vanno passati per toMember, non usati direttamente come Member (email mancante).
  members: MemberDto[] | null;
}

/** Team di sito con i relativi membri, usato dalla tab Team. */
export interface Team {
  teamId: number;
  groupId: number | null;
  name: string;
  description: string;
  members: Member[];
}

/**
 * Payload per la creazione di un team (POST /teams/{groupId}, operationId addTeam).
 * `groupId` è il sito in cui creare il team (il sito corrente dal pannello); nel body
 * viaggiano solo nome e descrizione.
 */
export interface CreateTeamPayload {
  groupId: number;
  name: string;
  description: string;
}

/**
 * Payload per la modifica di un team esistente (PATCH /team/{teamId}, operationId updateTeam):
 * aggiorna nome e descrizione.
 */
export interface UpdateTeamPayload {
  teamId: number;
  name: string;
  description: string;
}

/** Mapper del Team ritornato dal backend verso il modello UI. */
const toTeam = (t: TeamDto): Team => ({
  teamId: t.teamId,
  groupId: t.groupId ?? null,
  name: t.name ?? '',
  description: t.description ?? '',
  members: (t.members ?? []).map(toMember),
});

export const teamApi = {
  /** Lista dei team del sito con i relativi membri. Non cached (va sempre fresca). */
  getGroupTeams: (groupId: number): Promise<Team[]> =>
    httpClient
      .get<Page<TeamDto>>(`/teams/${groupId}`)
      .then((r) => r.data.items.map(toTeam)),

  /**
   * Crea un nuovo team nel sito (POST /teams/{groupId}); nel body solo nome e descrizione.
   * Solo per amministratori (enforce lato server). Ritorna il Team creato.
   */
  createTeam: ({ groupId, name, description }: CreateTeamPayload): Promise<Team> =>
    httpClient
      .post<TeamDto>(`/teams/${groupId}`, { name, description })
      .then((r) => toTeam(r.data)),

  /**
   * Modifica nome/descrizione di un team esistente (PATCH /team/{teamId}). Solo per
   * amministratori. Ritorna il Team aggiornato.
   */
  updateTeam: ({ teamId, name, description }: UpdateTeamPayload): Promise<Team> =>
    httpClient
      .patch<TeamDto>(`/team/${teamId}`, { name, description })
      .then((r) => toTeam(r.data)),

  /** Elimina un team (DELETE /team/{teamId}). Solo per amministratori. */
  deleteTeam: (teamId: number): Promise<void> =>
    httpClient.delete(`/team/${teamId}`).then(() => undefined),

  /**
   * Aggiunge un utente ai membri del team (POST /team/{teamId}/member/{userId}). L'utente deve
   * essere membro del sito del team. Solo per amministratori. Ritorna il Team aggiornato.
   */
  addMember: (teamId: number, userId: number): Promise<Team> =>
    httpClient
      .post<TeamDto>(`/team/${teamId}/member/${userId}`)
      .then((r) => toTeam(r.data)),

  /**
   * Rimuove un utente dai membri del team (DELETE /team/{teamId}/member/{userId}). Solo per
   * amministratori. Ritorna il Team aggiornato.
   */
  removeMember: (teamId: number, userId: number): Promise<Team> =>
    httpClient
      .delete<TeamDto>(`/team/${teamId}/member/${userId}`)
      .then((r) => toTeam(r.data)),
};
